import { Component, ElementRef, ViewChild, Inject, LOCALE_ID } from '@angular/core';

import Chart from "chart.js/auto";
import { ChartType } from 'chart.js';
import 'chartjs-adapter-moment';
import annotationPlugin from 'chartjs-plugin-annotation';
import { CommonModule } from "@angular/common";
import { FeatherModule } from "angular-feather";
import { showNotification } from '../../events';
import { FormsModule } from "@angular/forms";
import { firstValueFrom } from "rxjs";
import { PlotsService } from "../../services/plots.service";
import { UtilService } from '../../services/util.service';
import { ChartService, TSDataUnsorted, ChartObject } from '../../services/chart.service';
import { TSDataSorted, PlotMonitors, PlotEvent, WeatherData } from '../../model/plot';
import { Monitor } from "../../model/assessment";
import { colorCodes } from '../../constants';

declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    customCanvasBackgroundColor?: {
      color?: string
    }
  }
}


Chart.register(annotationPlugin);

@Component({
    selector: 'app-admin-charts',
    templateUrl: './admin-charts.component.html',
    imports: [
        CommonModule,
        FeatherModule,
        FormsModule
    ],
    styleUrls: ['./admin-charts.component.scss']
})

export class AdminChartsComponent {

  @ViewChild('canvas') canvas: ElementRef | undefined;

  isLoadingData = false;
  isDataLoaded = false;
  filtersMinimized = false;
  allData: TSDataUnsorted[] | null = null;
  activeIndex: number = 0;
  dataFilters: any = {
    type: [],
    id: []
  }

  sources: { id: string; label: string }[] = [{
    id: 'SENTINEL',
    label: 'Sentinel'
    }, {
      id: 'PLANET',
      label: 'Planet'
    },
    {
      id: 'lockout',
      label: $localize`:@@lockoutPeriod:Lockout period`
    }, {
      id: 'validEvents',
      label: $localize`:@@validEvents:Events (valid)`
    },
    {
      id: 'invalidEvents',
      label: $localize`:@@invalidEvents:Events (invalid)`
    },
    {
      id: 'precipitation',
      label: $localize`:@@precipitation:Precipitation`
    },
    {
      id: 'temperature',
      label: $localize`:@@temperature:Temperature`
    }
  ];

  chart: Chart | undefined;

  get objectIDsString(): string {
    return this.dataFilters.id.join('; ');
  };
  constructor(private plotsService: PlotsService,
    private util: UtilService,
    private chartsService: ChartService,
    @Inject(LOCALE_ID) private locale: string) {
  }


  toggleMinimizeFilters() {
    this.filtersMinimized = !this.filtersMinimized;
  }


  async requestData() {
    this.activeIndex = 0;
    this.isLoadingData = true;
    this.isDataLoaded = false;
    this.allData = null;
    const ndviData = await firstValueFrom(this.plotsService.getNdviDataMultiple(this.dataFilters.id));

    if (ndviData.length > 0) {
      this.allData = [];
      for (const element of ndviData) {
        let plotMonitors: PlotMonitors | undefined = undefined;
        let lockoutMonitor: Monitor | undefined = undefined;
        let allEvents: PlotEvent[] | null = null;
        let allWeather: WeatherData[] | null = null;

        if (this.dataFilters.type.includes('lockout')) {
          plotMonitors = await firstValueFrom(this.plotsService.getPlotMonitors(element.id));
          lockoutMonitor = await firstValueFrom(this.plotsService.getMonitor('SPERR_BRA'));
        }
        if (this.dataFilters.type.includes('validEvents') || this.dataFilters.type.includes('invalidEvents')) {
          allEvents = await firstValueFrom(this.plotsService.getPlotEvents(element.id));
        }
        if (this.dataFilters.type.includes('precipitation') || this.dataFilters.type.includes('temperature')) {
          allWeather = await firstValueFrom(this.plotsService.getWeatherData(element.id, this.dataFilters.type.includes('precipitation'), this.dataFilters.type.includes('temperature')));
        }
        
        this.allData!.push({
          ndviData: element,
          plotMonitors: plotMonitors ? plotMonitors : undefined,
          lockoutMonitor: lockoutMonitor ? lockoutMonitor : undefined,
          allEvents: allEvents ? allEvents : null,
          allWeather: allWeather ? allWeather : null
        });

        console.log(this.allData);
      };
      
      console.log("create chart", this.allData);
      this.toggleMinimizeFilters();
      this.prepareChartData(this.allData[this.activeIndex]);
    }

    this.isLoadingData = false;
    this.isDataLoaded = true;
  }


  async prepareChartData(plotData: TSDataUnsorted) {
    console.log('prepareChartData', plotData);
    await this.clearChart();

    const planetNdvi: ChartObject[] = [];
    const sentinelNdvi: ChartObject[] = [];
    const precipitation: ChartObject[] = [];
    const temperature: ChartObject[] = [];
    let events: PlotEvent[] = [];

    for (const measurement of plotData.ndviData.measurementsNdvi) {
      if (measurement.source === 'PLANET' && this.dataFilters.type.includes('PLANET')) {
        planetNdvi.push({ date: new Date(measurement.date), value: measurement.median });
      } else if (measurement.source === 'SENTINEL' && this.dataFilters.type.includes('SENTINEL')) {
        sentinelNdvi.push({ date: new Date(measurement.date), value: measurement.median });
      }
    }

    if (plotData.allWeather) {
      plotData.allWeather.forEach(entry => {
        if (this.dataFilters.type.includes('precipitation')) {
          precipitation.push({ date: new Date(entry.date), value: entry.precipitation });
        }
        if (this.dataFilters.type.includes('temperature')) {
          temperature.push({ date: new Date(entry.date), value: entry.temperature });
        }
      });
    }

    if ((this.dataFilters.type.includes('validEvents') || this.dataFilters.type.includes('invalidEvents')) && plotData.allEvents) {
      events = this.getEvents(plotData.allEvents);
    }

    const {currentYear, interpolatedData} = await this.chartsService.interpolateChartData(planetNdvi, sentinelNdvi, [], precipitation, temperature);

    if (this.dataFilters.type.includes('lockout') && plotData.plotMonitors && plotData.lockoutMonitor) {
      const isLockoutShown = this.findSperrBra(plotData.plotMonitors);

      if (isLockoutShown && plotData.lockoutMonitor && plotData.lockoutMonitor.dateStart && plotData.lockoutMonitor.dateEnd) {
        plotData.lockoutMonitor.dateStartParsed = this.util.getDateParsed(plotData.lockoutMonitor.dateStart, currentYear);
        plotData.lockoutMonitor.dateEndParsed = this.util.getDateParsed(plotData.lockoutMonitor.dateEnd, currentYear);
      }
    }
    
    await this.drawChart(interpolatedData, sentinelNdvi, planetNdvi,
      plotData.ndviData.id, plotData.lockoutMonitor ? plotData.lockoutMonitor : undefined, events);
  }


  private getEvents(allEvents: PlotEvent[]) {
    const events = [];
    if (this.dataFilters.type.includes('validEvents')) {
      events.push(...allEvents.filter(event => event.valid));
    } 
    if (this.dataFilters.type.includes('invalidEvents')) {
      events.push(...allEvents.filter(event => !event.valid));
    }
    return events;
  }

  private async clearChart() {
    this.chart?.destroy();
  }


  async drawChart(data: TSDataSorted[], sentinelOriginal: ChartObject[],
    planetOriginal: ChartObject[], 
    plotId: number,
    lockout: Monitor | undefined, events: PlotEvent[]) {

    await this.clearChart();

    if (data.length > 0 && this.canvas) {
      const chartData: { labels: any[], datasets: any[], allAnnotations: any[] } = await this.chartsService.createChartData(data, sentinelOriginal,
        planetOriginal, undefined, undefined, lockout, events, undefined, undefined);

      const labels = chartData.labels;
      const datasets = chartData.datasets;
      const allAnnotations = chartData.allAnnotations;

      let yAxisName = 'NDVI';
      const y2AxisItems = [];
      if (this.dataFilters.type.includes('precipitation')) {
        y2AxisItems.push($localize`:@@precipitationLabel:Precipitation in mm`);
      }
      if (this.dataFilters.type.includes('temperature')) {
        y2AxisItems.push($localize`:@@temperatureLabel:Temperature in °C`);
      }
      const y2AxisName = y2AxisItems.join(' / ');

      this.chart = new Chart(this.canvas.nativeElement, {
        type: 'line',
        data: {
          labels,
          datasets
        },
        options: {
          locale: this.locale,
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            y: {
              title: {
                display: true,
                text: yAxisName
              },
              min: 0,
              max: 1.0
            },
            y1: this.dataFilters.type.includes('precipitation') || this.dataFilters.type.includes('temperature') ? {
              min: -10,
              max: 100,
              title: {
                display: true,
                text: y2AxisName
              },
              position: 'right',
              grid: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              },
            } : undefined,
            x: {
              title: {
                display: true,
                text: 'Datum'
              },
              min: labels[0],
              max: labels[labels.length - 1],
              type: 'time',
              time: {
                unit: 'month',
                tooltipFormat:'DD.MM.YYYY',
                displayFormats: {
                  month: 'D.MM.YY'
                }
              },
              grid: {
                color: colorCodes.grid_line_start
              }
            }
          },
          plugins: {
            legend: {
              display: true,
              onClick: function(event, legendItem) {}
            },
            title: {
              display: true,
              text: 'ID: ' + plotId.toString()
            },
            customCanvasBackgroundColor: {
              color: 'white',
            },
            annotation: {
              annotations: allAnnotations
            },
          },
        },
        plugins: [{
          id: 'customCanvasBackgroundColor',
          beforeDraw: (chart, args, options) => {
            const { ctx } = chart;
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = options['color'] || colorCodes.chart_canvas_background;
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
          }
        }]
      });
      this.chart.update();
    }
  }


  resetDataFilters() {
    this.isDataLoaded = false;
    this.dataFilters = {
      type: [],
      id: []
    }
    this.setObjectIDsString('');
    this.allData = null;
    this.activeIndex = 0;
    this.clearChart();
  }

  setObjectIDsString(objectIDsString: string) {
    if (objectIDsString.match(/[\d\s;]+/)) {
      this.dataFilters.id = objectIDsString.split(';')
        .map(objectID => Number.parseInt(objectID.trim(), 10))
        .filter(objectId => !isNaN(objectId));
    } else if (!objectIDsString.match(/^\s*$/)) {
      showNotification.next({
        title: 'Fehler',
        message: 'Objekt-IDs dürfen nur aus Zahlen bestehen. Bei der Eingabe mehrerer Objekt-IDs müssen diese durch Semikolons voneinander getrennt werden.',
        class: 'is-danger'
      });
    }
  }

  exportChart() {
    if (this.allData && this.allData.length > 0 && this.allData[this.activeIndex]) {
      const plotId = this.allData![this.activeIndex].ndviData!.id;
      const declaredCode = this.allData[this.activeIndex].ndviData!.nCodeDeclared.code ? this.allData[this.activeIndex].ndviData!.nCodeDeclared.code!.toString() : "";
  
      const methods = this.dataFilters.type.join('_')
      const chartDataURL = this.chart!.toBase64Image();
  
      const link = document.createElement('a');
      link.href = chartDataURL;
      link.download = `${plotId.toString()}_${declaredCode}_${methods}.png`;
      link.click();
    }
  }

  findSperrBra(plotMonitors: PlotMonitors) {
    if (plotMonitors && plotMonitors.finalResults.length > 0) {
      return plotMonitors.finalResults.find(item => item.monitor === 'SPERR_BRA');
    } else {
      return false;
    }
  }

  moveChart(direction: number) {
    console.log("moveChart", direction);
    if (this.allData && this.allData.length > 0) {
      this.activeIndex = this.activeIndex + direction;
      if (this.activeIndex < 0) {
        this.activeIndex = this.allData.length - 1;
      } else if (this.activeIndex >= this.allData.length) {
        this.activeIndex = 0;
      }
      this.prepareChartData(this.allData[this.activeIndex]);
    }
  }


  queryButtonDisabled() {
    return this.dataFilters.id.length === 0 || (this.dataFilters.type.indexOf('PLANET') < 0 && this.dataFilters.type.indexOf('SENTINEL') < 0);
  }
}


